/**
 * Provides styles for the navigation components
 */
import styled from 'styled-components';
import {
  BorderProps,
  ColorProps,
  LayoutProps,
  PositionProps,
  border,
  color,
  layout,
  position,
} from 'styled-system';

import { Colors } from '@nakedplatform/ui';
import { BreakPoints } from 'theme/breakpoints';
import { FontWeight } from 'theme/fonts';
import { Image } from 'theme/images';
import { Container } from 'theme/layout';
import { VARIANTS } from 'theme/variants';

const { translateX } = VARIANTS.transforms;

export const AvatarContainer = styled.div`
  width: 50px;
  height: 50px;
  display: block;
  margin-right: 11px;
  position: relative;

  & ::after {
    content: '';
    display: block;
    width: 11px;
    height: 11px;
    border-radius: 5.5px;
    position: absolute;
    top: 19.5px;
    right: -5.5px;
    background-color: ${Colors.emerald};
  }
`;

export const Avatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 25px;
  display: block;
  background-color: ${Colors.lightGrey};
`;

export const StickyNav = styled(Container)`
  position: fixed;
  width: 100%;
  z-index: 1071;
`;

export const NavSection = styled(Container)<AppNavigatorSurfaceProps & BorderProps>`
  width: 100%;
  position: ${({ overlay }: AppNavigatorSurfaceProps) => (overlay ? 'absolute' : 'relative')};
  top: 0px;
  right: 0px;
  left: 0px;
  height: 88px;
  display: flex;
  align-items: center;
  transition-property: border-top-color;
  transition-timing-function: ease;
  transition-delay: 0s;
  transition-duration: 0.3s;
  z-index: ${({ overlay }: AppNavigatorSurfaceProps) => (overlay ? '1006' : 'auto')};
  background-color: ${({ overlay, transparent }: AppNavigatorSurfaceProps) =>
    overlay || transparent ? Colors.transparent : Colors.white};
  border-bottom: ${({ hideBorder }: AppNavigatorSurfaceProps) =>
    hideBorder ? 'none !important' : undefined};
  ${border}
`;

export const MenuContainer = styled('div')`
  display: flex;
  position: relative;
  float: right;
  align-items: center;
  padding-right: 10px;
  margin-left: auto;
`;

export const MenuList = styled('ul')`
  display: inline-block;
  margin: 0px;
  padding: 0px;
  list-style: none;
  padding-right: 1.2em;
  transition: transform 0.3s ease-in-out;

  @media (max-width: ${BreakPoints.medium}) {
    position: fixed;
    top: 0px;
    bottom: 0px;
    left: 0px;
    background: ${Colors.thunder};
    width: 80%;
    transform: translateX(-100%);
    z-index: 1002;
    ${translateX};
    padding-top: 3em;
    padding-left: 3em;
  }
`;

export const MenuItem = styled('li')<PositionProps>`
  ${position};
  display: inline-block;

  &:hover ul {
    display: block;
  }

  @media (max-width: ${BreakPoints.medium}) {
    display: block;
  }
`;

export const Link = styled('a')`
  cursor: pointer;
  text-decoration: none;
`;

export const MenuLink = styled(Link)<MenuLinkProps>`
  display: block;
  font-size: 16px;
  color: ${Colors.thunder};
  font-weight: ${FontWeight.medium};
  padding: 16px 24px;
  transition:
    font-weight 0.2s ease 0s,
    color 0.3s ease-in-out;
  letter-spacing: 0.45px;
  position: relative;
  background-color: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(35, 31, 32, 0.05);
    border-radius: 28px;
    z-index: -1; /* Place it behind the content */
    transform: scale(0.8); /* Start fully scaled down */
    transition:
      transform 0.2s ease-in-out,
      opacity 0.2s ease-in-out; /* Animate the transform and opacity */
    opacity: 0; /* Start invisible */
  }

  ${Image} {
    transition: transform 0.3s ease-in-out;
  }

  &:hover {
    cursor: ${props => (props.isDropdownLink ? 'default' : 'pointer')};

    ${Image} {
      transform: rotate(-180deg);
      transition: transform 0.3s ease-in-out;
    }

    &::before {
      z-index: 10;
      transform: scale(1); /* Scale to normal size */
      opacity: 1; /* Make it visible */
    }
  }

  @media (max-width: ${BreakPoints.medium}) {
    color: ${Colors.white};
    padding: 16px 0;
    display: ${props => (props.hideOnMobile ? 'none' : 'block')};
    font-weight: ${props => (props.isBold ? FontWeight.bold : FontWeight.light)};
    &:hover {
      cursor: pointer;
    }
  }
`;

export const BurgerMenuLine = styled('div')`
  height: 2px;
  width: 16px;
  background-color: ${Colors.white};
  margin-top: 3px;
  border-radius: 1px;
`;

export const MenuButton = styled(Link)`
  position: absolute;
  width: 56px;
  height: 56px;
  border-top-right-radius: 28px;
  border-bottom-right-radius: 28px;
  background-color: ${Colors.thunder};
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  flex-direction: column;

  &:focus {
    outline: 0;
  }
  &:active {
    background-color: ${Colors.alto};
  }
`;

export const BackButton = styled.button<AppNavigatorToneProps>`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  width: 66px;
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
  border: none;
  background-color: ${({ transparent }: AppNavigatorToneProps) =>
    transparent ? Colors.transparent : Colors.white};
  cursor: pointer;
`;

export const CloseButton = styled.button<AppNavigatorToneProps>`
  width: 35px;
  height: 35px;
  border-radius: 17.5px;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 12.5px;
  border-width: 1px;
  outline: none;
  border: none !important;
  right: 18px;
  background-color: ${({ transparent }: AppNavigatorToneProps) =>
    transparent ? Colors.transparent : Colors.white};
  cursor: pointer;
  border-style: solid;
`;

export const LogoLink = styled(Link)<LogoLinkProps>`
  display: inline-block;
  margin-left: ${({ centerAlign, leftAlign }: LogoLinkProps) => {
    if (centerAlign) {
      return 'auto';
    }

    return leftAlign ? '24px' : '45.5px';
  }};
  margin-right: ${({ centerAlign }: LogoLinkProps) => (centerAlign ? 'auto' : '35px')};
  outline: none;
  vertical-align: middle;
  padding: 13px 0px;
  cursor: pointer;
  line-height: 1;

  @media (max-width: ${BreakPoints.medium}) {
    padding: 0px;
    margin-left: ${({ centerAlign, leftAlign }: LogoLinkProps) => {
      if (centerAlign) {
        return 'auto';
      }

      return leftAlign ? '24px' : '72px';
    }};
    & i {
      vertical-align: middle;
    }
  }
`;

export const RoseBoldText = styled.span`
  font-size: 17px;
  font-weight: ${FontWeight.medium};
  color: ${Colors.black};
`;

export const RoseLightText = styled.span`
  font-weight: ${FontWeight.light};
`;

export const SocialGroup = styled('ul')`
  display: block;
  margin: 40px 0px;
  padding: 0px;
  list-style: none;
  position: absolute;
  bottom: 0px;
  right: 0px;
  left: 0px;
  text-align: center;
  list-style: none;
  background-color: ${Colors.thunder};
`;

export const SocialItem = styled('li')`
  display: inline-block;
  padding: 10px 15px;
`;

export const SocialLink = styled('a')`
  text-decoration: none;
  outline: none;
  &:focus {
    outline: none;
  }
`;

export const DropDownContainer = styled('ul')`
  position: absolute;
  background: #fff;
  width: 185px;
  z-index: 999;
  list-style-type: none;
  transform-origin: 50% 0;
  transition: all 300mss;
  box-shadow: 0 0 15px 0 rgba(0, 0, 0, 0.21);
  left: 50%;
  top: 100%;
  padding: 10px 10px 10px 10px;
  margin: 0px;
  border-radius: 28px;
  border: 1px solid #dedede;
  transform: translateX(-50%);
  display: none;

  & ::before {
    content: '';
    position: absolute;
    width: 37px;
    height: 12px;
    top: -12px;
    left: 49%;
    margin-left: -10px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 12px solid white;
  }

  @media (max-width: ${BreakPoints.medium}) {
    display: block;
    position: static;
    left: 0;
    top: 0;
    transform: translateX(0);
    background: transparent;
    border: none;
    padding: 0px;
    border-radius: 0px;
    box-shadow: none;

    & ::before {
      display: none;
      position: static;
    }

    & ::after {
      content: '';
      display: block;
      width: 100%;
      height: 1px;
    }
  }
`;

export const DropDownItem = styled('li')`
  padding: 0px 0px;
  font-weight: ${FontWeight.regular};
`;

export const DropDownLink = styled(Link)`
  position: relative;
  overflow: hidden; // Ensure the pseudo-element does not extend outside
  display: block;
  color: ${Colors.thunder};
  font-size: 16px;
  padding: 18px 20px;
  width: 100%;
  text-align: center;
  background-color: transparent; // Initial background color
  transition: color 0.3s ease-in-out; // Animate text color if needed

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(35, 31, 32, 0.05);
    z-index: -1; // Place it behind the content
    transform: scale(0.8); // Start fully scaled down
    transition: transform 0.2s ease-in-out; // Animate the transform
    border-radius: 28px; // Match parent's border-radius
    opacity: 0; // Start invisible
  }

  &:hover::before {
    transform: scale(1); // Scale to normal size
    opacity: 1; // Make it visible
  }

  @media (max-width: ${BreakPoints.medium}) {
    color: ${Colors.white};
    text-align: left;
    font-weight: ${FontWeight.bold};
    padding: 16px 0px 16px 0px;
  }
`;

export const DropDownLinkComingSoon = styled(DropDownLink)`
  color: ${Colors.silverChalice}!important;
  &:hover {
    color: ${Colors.silverChalice}!important;
  }
`;

export const ComingSoonContainer = styled(Container)`
  font-size: 10px;
  font-weight: normal;
  color: ${Colors.gray};
  letter-spacing: 0.5px;
  background-color: ${Colors.gallery};
  border-radius: 10px;
  height: 20px;
  width: max-content;
  padding: 0px 8px 0px 8px;
  cursor: pointer;
`;

export const MobileLinks = styled('div')`
  display: none;

  @media (max-width: ${BreakPoints.medium}) {
    display: block;
  }
`;

export const MenuCloseButton = styled('button')`
  display: none;
  position: absolute;
  top: 15px;
  right: 15px;
  outline: none;
  background: transparent;
  border: none;

  &:focus {
    outline: none;
  }
  @media (max-width: ${BreakPoints.medium}) {
    display: block;
  }
`;

export const Overlay = styled('div')<LayoutProps & ColorProps>`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  display: none;
  transition: 1s opacity ease-in;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 1002;
  ${color};
  ${layout};
`;

export const ItemList = styled('ul')`
  display: inline-block;
  margin: 0px;
  padding: 0px;
  list-style: none;
  padding-right: 1.2em;
  transition: transform 0.3s ease-in-out;
  position: fixed;
  top: 0px;
  bottom: 0px;
  left: 0px;
  background: ${Colors.thunder};
  width: 241px;
  transform: translateX(-100%);
  z-index: 1002;
  ${translateX};
  padding-top: 3em;
  padding-left: 2em;
`;

export const ListItem = styled('li')<PositionProps>`
  ${position};
  display: block;
`;

export const ItemLink = styled(Link)`
  display: block;
  font-size: 15px;
  font-weight: normal;
  transition: font-weight 0.2s ease 0s;
  line-height: normal;
  letter-spacing: 0.45px;
  color: ${Colors.white};
  padding: 13px 0;

  &:hover {
    color: ${Colors.emerald};
    cursor: pointer;
  }

  &.sub-menu:hover {
    color: ${Colors.white};
    cursor: default;
  }
`;

export const MenuOverlay = styled(Overlay)`
  @media (min-width: ${BreakPoints.medium}) {
    display: none !important;
  }
`;

export const SubItemList = styled('ul')`
  padding: 0px 0px 0px 1.5em;
  margin: 0px;
  list-style-type: none;
`;

export const SubItemListItem = styled('li')`
  padding: 10px 0px;
`;

export const SubItemLink = styled(Link)`
  display: block;
  font-size: 15px;
  font-weight: ${FontWeight.light};
  color: ${Colors.white};
  text-align: left;

  &:hover {
    color: ${Colors.emerald};
  }
`;

export const SocialMediaSection = styled('div')`
  max-width: 100%;
`;

export const SocialMediaLink = styled('a')`
  text-decoration: none;
  margin-right: 12px;
  cursor: pointer;
`;

/** MenuLink Props */
interface MenuLinkProps extends ColorProps {
  /** Flag to know if current MenuLink is a dropdown or not */
  isDropdownLink?: boolean;
  /** If true, will bold the text */
  isBold?: boolean;
  /** If true, will hide the element on mobile */
  hideOnMobile?: boolean;
}

interface LogoLinkProps {
  /** If true, will center align the Header Logo */
  centerAlign?: boolean;
  /** If true, will align the logo closer to the left edge */
  leftAlign?: boolean;
}

interface AppNavigatorSurfaceProps {
  /** If true, places the navigator above the page chrome instead of in normal flow */
  overlay?: boolean;
  /** If true, removes the background fill from the navigator */
  transparent?: boolean;
  /** If true, removes the default bottom border */
  hideBorder?: boolean;
}

interface AppNavigatorToneProps {
  /** If true, removes the default solid background from the control */
  transparent?: boolean;
}
